import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import DocEditor from './DocEditor';
import DocList from './DocList';
import NotesView from './NotesView';

const c = theme.colors;

// Las acciones del cajero. `ready` indica si ya está implementada; el resto
// muestra un panel "en construcción" hasta su fase.
const ACTIONS = [
  { key: 'facturar', label: 'Facturar', desc: 'Crear una factura de venta', icon: '🧾', ready: true, view: 'editor', type: 'factura', typeLabel: 'Factura', hasPayment: true },
  { key: 'ver_facturas', label: 'Ver facturado', desc: 'Facturas emitidas y sus estados', icon: '📄', ready: true, view: 'list', type: 'factura', typeLabel: 'Facturas emitidas' },
  { key: 'facturar_pos', label: 'Facturar POS', desc: 'Venta rápida en punto de venta', icon: '🛒', ready: true, view: 'editor', type: 'pos', typeLabel: 'Factura POS', hasPayment: true },
  { key: 'ver_pos', label: 'Ver facturado POS', desc: 'Facturas POS emitidas', icon: '🧮', ready: true, view: 'list', type: 'pos', typeLabel: 'Facturas POS emitidas' },
  { key: 'cotizar', label: 'Crear cotización', desc: 'Cotización para un cliente', icon: '📝', ready: true, view: 'editor', type: 'cotizacion', typeLabel: 'Cotización', hasPayment: false },
  { key: 'ver_cotizaciones', label: 'Ver cotizaciones', desc: 'Cotizaciones creadas', icon: '📋', ready: true, view: 'list', type: 'cotizacion', typeLabel: 'Cotizaciones creadas' },
  { key: 'nota_credito', label: 'Notas crédito', desc: 'Crear y ver notas crédito', icon: '➖', ready: true, view: 'notes', type: 'nota_credito', typeLabel: 'Nota crédito' },
  { key: 'nota_debito', label: 'Notas débito', desc: 'Crear y ver notas débito', icon: '➕', ready: true, view: 'notes', type: 'nota_debito', typeLabel: 'Nota débito' },
];

function Cajero({ token }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Prellenado al facturar un pedido desde la página de Pedidos:
  // { action, customer, items, billId }. Se consume una sola vez.
  const [prefill, setPrefill] = useState(() => location.state?.cajeroPrefill || null);
  const [active, setActive] = useState(() => (location.state?.cajeroPrefill?.action) || null);

  // Limpiar el state de navegación para que al recargar o volver no se vuelva a
  // disparar el prellenado.
  useEffect(() => {
    if (location.state?.cajeroPrefill) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const back = () => { setActive(null); setPrefill(null); };

  if (active) {
    const a = ACTIONS.find((x) => x.key === active);
    if (a.ready && a.view === 'editor') {
      const p = prefill && prefill.action === a.key ? prefill : null;
      return <DocEditor token={token} type={a.type} typeLabel={a.typeLabel} hasPayment={a.hasPayment} onBack={back}
        initialCustomer={p?.customer} initialItems={p?.items} billId={p?.billId} appointmentId={p?.appointmentId} />;
    }
    if (a.ready && a.view === 'list') {
      return <DocList token={token} type={a.type} typeLabel={a.typeLabel} onBack={back} />;
    }
    if (a.ready && a.view === 'notes') {
      return <NotesView token={token} noteType={a.type} typeLabel={a.typeLabel} onBack={back} />;
    }
    return (
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>
        <button onClick={back} style={{ background: 'none', border: `1px solid ${c.border}`, color: c.text, borderRadius: '7px', padding: '7px 14px', cursor: 'pointer', fontSize: '0.85rem', marginBottom: '18px', fontFamily: 'inherit' }}>← Volver al cajero</button>
        <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>{a.icon}</div>
          <h2 style={{ color: c.primary, margin: '0 0 6px' }}>{a.label}</h2>
          <p style={{ color: c.textMuted }}>Esta sección se habilita en la {a.phase}.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h1 style={{ color: c.primary, fontSize: '1.5rem', margin: '0 0 4px' }}>Cajero</h1>
          <p style={{ color: c.textMuted, fontSize: '0.9rem', margin: 0 }}>Factura, cotiza y gestiona tus documentos.</p>
        </div>
        <Link to="/cajero-config" style={{ color: c.primary, textDecoration: 'none', fontSize: '0.85rem', border: `1px solid ${c.primary}`, borderRadius: '7px', padding: '8px 14px' }}>⚙ Configuración</Link>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px', marginTop: '22px' }}>
        {ACTIONS.map((a) => (
          <button key={a.key} onClick={() => setActive(a.key)} style={{
            textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
            background: c.surface, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '18px',
            color: c.text, position: 'relative',
          }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = c.primary)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = c.border)}
          >
            <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>{a.icon}</div>
            <div style={{ fontWeight: 600, fontSize: '0.98rem' }}>{a.label}</div>
            <div style={{ color: c.textMuted, fontSize: '0.82rem', marginTop: '3px' }}>{a.desc}</div>
            {!a.ready && <span style={{ position: 'absolute', top: 12, right: 12, fontSize: '0.68rem', color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: '10px', padding: '2px 7px' }}>{a.phase}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default Cajero;
